import * as React from 'react';
import { useDispatch } from 'react-redux';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer } from './hooks/useLocalizer';
import { UploadedFile } from './types';
import { DataCollectionAiAgentOperationType } from '../../../store/Models/Surveys/SurveyDataModel';
import {
    actionCreators as aiActionCreators
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import {
    SupplierResponseAlreadyProvidedResult,
    SurveyResponseSubmittedOfflineImportUnavailableMessage
} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { showSurveyResponseSubmittedOfflineImportUnavailablePopup } from '../../Survey/SurveyResponseSubmittedOfflineImportUnavailableModal';

const retryIconSrc = "/images/supplier-ai-agent/retry-icon.svg";

interface AITryAgainButtonProps {
    requestId?: string;
    operationId?: string;
    uploadedFiles: UploadedFile[];
    operationType: DataCollectionAiAgentOperationType;
    onError?: (message: string) => void;
    onClearError?: () => void;
    localizer?: IStringLocalizer;
    className?: string;
}

/**
 * AITryAgainButton Component
 * 
 * Self-contained button that handles:
 * - Try again button UI
 * - Clearing error state and retrying AI processing with existing files
 * 
 */
export const AITryAgainButton: React.FC<AITryAgainButtonProps> = ({
    requestId,
    operationId,
    uploadedFiles,
    operationType,
    onError,
    onClearError,
    localizer,
    className
}) => {
    const dispatch = useDispatch();
    const localize = useLocalizer(localizer);

    const showSubmittedResponseUnavailableMessage = (message?: string) => {
        if (showSurveyResponseSubmittedOfflineImportUnavailablePopup()) {
            return;
        }

        if (onError) {
            onError(localize(message || SurveyResponseSubmittedOfflineImportUnavailableMessage));
        }
    };

    /**
     * Handles try again action by clearing error state and retrying processing
     */
    const handleTryAgain = async () => {
        try {
            // Clear error state first
            if (onClearError) {
                onClearError();
            }

            // Validate prerequisites
            if (!requestId) {
                if (onError) {
                    onError(localize("Invalid request. Please refresh the page and try again."));
                }
                return;
            }

            if (!operationId) {
                if (onError) {
                    onError(localize("Invalid operation. Please refresh the page and try again."));
                }
                return;
            }

            // Retrigger AI workflow using the existing operation configuration
            const result = await dispatch(
                aiActionCreators.retriggerAIWorkflow(
                    requestId,
                    operationId
                ) as any
            );

            if (!result.success) {
                if (result.result === SupplierResponseAlreadyProvidedResult) {
                    showSubmittedResponseUnavailableMessage(result.resultMessage);
                    return;
                }

                const errorKey = result.result === 'Error'
                    ? "AI processing failed. Please try again."
                    : "Failed to start AI processing. Please try again.";

                if (onError) {
                    onError(localize(errorKey));
                }
                return;
            }

        } catch (error) {
            if (onError) {
                onError(
                    localize("An unexpected error occurred during AI processing. Please try again.")
                );
            }
        }
    };

    return (
        <button
            type="button"
            onClick={handleTryAgain}
            className={`btn btn-toxnot-primary ai-try-again-button${className ? ` ${className}` : ''}`}
        >
            <img src={retryIconSrc} width="16" height="16" className="ai-retry-icon" aria-hidden="true" alt="" />
            {localize("Try Again")}
        </button>
    );
};

export default AITryAgainButton;
